/**
 * AdminCoupons.tsx — lista kuponów rabatowych (F7a T7.7).
 *
 * Model filtrowania „jak w katalogu modułów" (poprawki-develop): ZERO górnego
 * dropdowna statusu — całe filtrowanie żyje w nagłówkach kolumn (współdzielony
 * `ConfigurableTable`, client-side na pobranej liście). Kod → szukajka tekstowa;
 * Typ rabatu i Status → dropdown wyboru w wierszu filtrów. Nad tabelą zostaje
 * tylko przycisk „+ Dodaj kupon" (brak kontrolki zmieniającej pobierany zbiór).
 *
 * Data: `GET /api/admin/coupons?limit=100&offset=0` (pobieramy do górnego limitu
 * pod client-side filtry per kolumna).
 * Akcje "Szczegóły" disabled — edycja / soft-delete kuponów od F8.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TicketPercent } from 'lucide-react';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

import { CreateCouponModal } from '../components/CreateCouponModal';

export type CouponStatus = 'active' | 'expired';
export type DiscountType = 'percentage' | 'fixed';

export interface CouponListItem {
  id: string;
  code: string;
  discountType: DiscountType;
  discountValue: number;
  maxUses: number | null;
  usesCount: number;
  validFrom: string | null;
  validUntil: string | null;
  status: CouponStatus;
  applicableModuleIds: string[] | null;
  createdAt: string;
  gcrecord: string | null;
}

interface CouponsResponse {
  coupons: CouponListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** Górny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

const STATUS_COLOR: Record<CouponStatus, BadgeColor> = {
  active: 'green',
  expired: 'gray',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDiscount(row: CouponListItem): string {
  if (row.discountType === 'percentage') {
    return `${row.discountValue}%`;
  }
  try {
    return (row.discountValue / 100).toLocaleString('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    });
  } catch {
    return `${(row.discountValue / 100).toFixed(2)} PLN`;
  }
}

export function AdminCoupons() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const couponsQuery = useQuery<CouponsResponse>({
    queryKey: ['admin', 'coupons'],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', String(LIST_FETCH_LIMIT));
      const res = await api.get<CouponsResponse>(`/admin/coupons?${params.toString()}`);
      return res.data;
    },
  });

  const statusLabel = (status: CouponStatus): string =>
    t(`admin.coupons.status${status.charAt(0).toUpperCase() + status.slice(1)}`);

  const discountTypeLabel = (type: DiscountType): string => t(`admin.coupons.discountType.${type}`);

  const columns = useMemo<ColumnDefinition<CouponListItem>[]>(
    () => [
      {
        id: 'code',
        accessor: (row) => row.code,
        header: t('admin.coupons.columns.code'),
        sortable: true,
        // Kolumna wolnotekstowa → szukajka (domyślnie filtrowalna).
        renderCell: (_value, row) => (
          <span className="font-mono text-xs font-semibold">{row.code}</span>
        ),
      },
      {
        id: 'discountType',
        accessor: (row) => row.discountType,
        header: t('admin.coupons.columns.discountType'),
        sortable: true,
        // Dwa typy → dropdown wyboru (client-side, exact-match).
        headerFilter: {
          category: 'select',
          options: [
            { value: 'percentage', label: discountTypeLabel('percentage') },
            { value: 'fixed', label: discountTypeLabel('fixed') },
          ],
        },
        renderCell: (_value, row) => (
          <Badge color={row.discountType === 'percentage' ? 'blue' : 'accent'}>
            {discountTypeLabel(row.discountType)}
          </Badge>
        ),
      },
      {
        id: 'discountValue',
        accessor: (row) => row.discountValue,
        header: t('admin.coupons.columns.discountValue'),
        sortable: true,
        align: 'right',
        // Wartość liczbowa — bez szukajki (fraza nie ma sensu).
        filterable: false,
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">{formatDiscount(row)}</span>
        ),
      },
      {
        id: 'uses',
        accessor: (row) => row.usesCount,
        header: t('admin.coupons.columns.uses'),
        sortable: true,
        align: 'right',
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">
            {row.usesCount}
            {row.maxUses !== null ? ` / ${row.maxUses}` : ''}
          </span>
        ),
      },
      {
        id: 'validFrom',
        accessor: (row) => row.validFrom ?? '',
        header: t('admin.coupons.columns.validFrom'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.validFrom)}</span>
        ),
      },
      {
        id: 'validUntil',
        accessor: (row) => row.validUntil ?? '',
        header: t('admin.coupons.columns.validUntil'),
        sortable: true,
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{formatDate(row.validUntil)}</span>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.coupons.columns.status'),
        sortable: true,
        // Stany rozłączne → dropdown wyboru (client-side, exact-match).
        headerFilter: {
          category: 'select',
          options: [
            { value: 'active', label: statusLabel('active') },
            { value: 'expired', label: statusLabel('expired') },
          ],
        },
        renderCell: (_value, row) => (
          <Badge color={STATUS_COLOR[row.status]}>{statusLabel(row.status)}</Badge>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const renderActions = (_row: CouponListItem) => (
    <div className="flex items-center justify-end">
      <Button variant="ghost" size="sm" disabled title={t('admin.coupons.detailsDisabledTooltip')}>
        {t('admin.coupons.details')}
      </Button>
    </div>
  );

  const data = couponsQuery.data;
  const rows = data?.coupons ?? [];

  return (
    <Layout title={t('admin.coupons.title')}>
      <section className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">{t('admin.coupons.title')}</h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.coupons.subtitle')}</p>
          </div>
          <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
            {t('admin.coupons.addButton')}
          </Button>
        </div>

        {couponsQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : couponsQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.coupons.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void couponsQuery.refetch()}
            >
              {t('admin.coupons.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<CouponListItem>
                tableId="admin-coupons"
                columns={columns}
                data={rows}
                getRowId={(row) => row.id}
                renderActions={renderActions}
                actionsColumnLabel={t('admin.coupons.columns.actions')}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={TicketPercent}
                emptyTitle={
                  rows.length === 0
                    ? t('admin.coupons.emptyNoData')
                    : t('admin.coupons.emptyFiltered')
                }
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>
            {data && (
              <p className="mt-3 text-xs text-text-muted">
                {t('admin.coupons.countLabel', { count: data.total })}
              </p>
            )}
          </>
        )}
      </section>

      <CreateCouponModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => {
          setIsCreateOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
        }}
      />
    </Layout>
  );
}
